import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { Header } from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import IdeaCard from '@/components/IdeaCard';
import IdeaDetailDialog from '@/components/IdeaDetailDialog';
import SaveIdeaDialog from '@/components/SaveIdeaDialog';
import ParticipantList from '@/components/ParticipantList';
import TripSettingsDialog from '@/components/TripSettingsDialog';
import PreferencesTab from '@/components/PreferencesTab';
import GroupProfileTab from '@/components/GroupProfileTab';
import VotesTab from '@/components/VotesTab';
import ItineraryTab from '@/components/ItineraryTab';
import BudgetTab from '@/components/BudgetTab';
import UpgradeModal from '@/components/UpgradeModal';
import TripDetailsSection from '@/components/TripDetailsSection';
import { useAuth } from '@/context/AuthContext';
import { usePolling } from '@/hooks/usePolling';
import { api } from '@/lib/api';
import { TRIP, IDEA, TRIP_SETTINGS, TRIP_STATUS, GROUP_PROFILE, UPGRADE } from '@/constants/testIds';
import { CATEGORIES } from '@/lib/categories';
import { ArrowLeft, Calendar, MapPin, Copy, Check, Users, Plus, Lightbulb, Share2, Lightbulb as Bulb, ListChecks, Vote, Settings, Sparkles, Loader2 } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';

function formatDateRange(start, end) {
    try {
        const s = parseISO(start), e = parseISO(end);
        if (s.getFullYear() === e.getFullYear()) {
            return `${format(s, 'MMM d')} – ${format(e, 'MMM d, yyyy')}`;
        }
        return `${format(s, 'MMM d, yyyy')} – ${format(e, 'MMM d, yyyy')}`;
    } catch { return `${start} – ${end}`; }
}

const EmptyTab = ({ title, description }) => (
    <div className="ns-card p-10 sm:p-14 text-center mt-6">
        <h3 className="font-display text-xl sm:text-2xl font-semibold tracking-tight">{title}</h3>
        <p className="text-[var(--ns-text2)] mt-2 max-w-md mx-auto leading-relaxed">{description}</p>
        <span className="inline-block mt-5 text-xs uppercase tracking-[0.18em] text-[var(--ns-text3)]">Coming next</span>
    </div>
);

function StatusPill({ icon: Icon, label, value, testid }) {
    return (
        <div
            data-testid={testid}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-[var(--ns-border)]"
        >
            <Icon size={14} className="text-[var(--ns-clay)]" />
            <span className="text-xs uppercase tracking-widest text-[var(--ns-text3)]">{label}</span>
            <span className="text-sm font-semibold text-[var(--ns-text)]">{value}</span>
        </div>
    );
}

function IdeasTab({ tripId, refreshKey, onOpenSave, currentUserId, tripOwnerId }) {
    const [ideas, setIdeas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [category, setCategory] = useState('all');
    const [sort, setSort] = useState('newest');
    const [detailIdea, setDetailIdea] = useState(null);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get(`/ideas/trip/${tripId}`);
            setIdeas(data.ideas || []);
        } catch { /* ignore */ }
        finally { setLoading(false); }
    }, [tripId]);

    useEffect(() => { load(); }, [load, refreshKey]);

    const filtered = useMemo(() => {
        let list = ideas;
        if (category !== 'all') list = list.filter(i => i.category === category);
        list = [...list].sort((a, b) => {
            if (sort === 'newest') return b.created_at.localeCompare(a.created_at);
            if (sort === 'oldest') return a.created_at.localeCompare(b.created_at);
            if (sort === 'category') return (a.category || '').localeCompare(b.category || '');
            return 0;
        });
        return list;
    }, [ideas, category, sort]);

    const counts = useMemo(() => {
        const map = {};
        ideas.forEach(i => { map[i.category] = (map[i.category] || 0) + 1; });
        return map;
    }, [ideas]);

    return (
        <div className="mt-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                <div className="flex items-center gap-3 flex-wrap">
                    <Select value={category} onValueChange={setCategory}>
                        <SelectTrigger data-testid={IDEA.tripCategoryFilter} className="h-10 rounded-full border-[var(--ns-border)] bg-white min-w-[160px]">
                            <SelectValue placeholder="All categories" />
                        </SelectTrigger>
                        <SelectContent className="rounded-2xl">
                            <SelectItem value="all">All categories ({ideas.length})</SelectItem>
                            {CATEGORIES.map(c => {
                                const Icon = c.icon;
                                return (
                                    <SelectItem key={c.value} value={c.value}>
                                        <span className="inline-flex items-center gap-2">
                                            <Icon size={14} style={{ color: c.color }} /> {c.label} ({counts[c.value] || 0})
                                        </span>
                                    </SelectItem>
                                );
                            })}
                        </SelectContent>
                    </Select>
                    <Select value={sort} onValueChange={setSort}>
                        <SelectTrigger data-testid={IDEA.tripSortSelect} className="h-10 rounded-full border-[var(--ns-border)] bg-white min-w-[140px]">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="rounded-2xl">
                            <SelectItem value="newest">Newest first</SelectItem>
                            <SelectItem value="oldest">Oldest first</SelectItem>
                            <SelectItem value="category">By category</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <Button
                    onClick={onOpenSave}
                    data-testid={IDEA.tripAddIdeaButton}
                    className="rounded-full h-11 px-5 text-white"
                    style={{ background: 'var(--ns-clay)' }}
                >
                    <Plus size={16} className="mr-2" /> Add idea
                </Button>
            </div>

            {loading ? (
                <div className="ns-card p-12 text-center text-[var(--ns-text2)]">Loading ideas…</div>
            ) : filtered.length === 0 ? (
                <div data-testid={IDEA.tripIdeasEmpty} className="ns-card p-10 sm:p-14 text-center">
                    <div className="mx-auto w-14 h-14 rounded-2xl flex items-center justify-center mb-4" style={{ background: 'var(--ns-soft)' }}>
                        <Lightbulb className="text-[var(--ns-clay)]" />
                    </div>
                    <h3 className="font-display text-xl font-semibold tracking-tight">
                        {ideas.length === 0 ? 'No ideas yet' : 'Nothing in this category'}
                    </h3>
                    <p className="text-[var(--ns-text2)] mt-2 max-w-md mx-auto">
                        {ideas.length === 0
                            ? 'Drop a link or type a place name — we will help you tag it.'
                            : 'Try a different filter or save a new idea.'}
                    </p>
                    <Button
                        onClick={onOpenSave}
                        className="mt-6 rounded-full h-11 px-6 text-white"
                        style={{ background: 'var(--ns-navy)' }}
                    >
                        <Plus size={16} className="mr-2" /> Save your first idea
                    </Button>
                </div>
            ) : (
                <div data-testid={IDEA.tripIdeasGrid} className="columns-2 lg:columns-3 gap-5">
                    {filtered.map(i => (
                        <IdeaCard
                            key={i.inspiration_id}
                            idea={i}
                            tripId={tripId}
                            onOpen={setDetailIdea}
                            onVoteChanged={load}
                            currentUserId={currentUserId}
                            tripOwnerId={tripOwnerId}
                            onChanged={(evt) => {
                                if (evt?.type === 'deleted') {
                                    setIdeas(prev => prev.filter(x => x.inspiration_id !== evt.ideaId));
                                } else if (evt?.type === 'updated' && evt.idea) {
                                    setIdeas(prev => prev.map(x => x.inspiration_id === evt.idea.inspiration_id ? { ...x, ...evt.idea } : x));
                                }
                            }}
                        />
                    ))}
                </div>
            )}

            <IdeaDetailDialog
                open={!!detailIdea}
                onOpenChange={(o) => { if (!o) setDetailIdea(null); }}
                idea={detailIdea}
                onVoteChanged={load}
            />
        </div>
    );
}

export default function TripDetail() {
    const { tripId } = useParams();
    const { user } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const [trip, setTrip] = useState(null);
    const [loading, setLoading] = useState(true);
    const [copied, setCopied] = useState(false);
    const [saveIdeaOpen, setSaveIdeaOpen] = useState(false);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    const [upgradeOpen, setUpgradeOpen] = useState(false);
    const [paymentChecking, setPaymentChecking] = useState(false);
    const [activeTab, setActiveTab] = useState('ideas');
    const [welcomeBannerOpen, setWelcomeBannerOpen] = useState(false);

    // Invite-signup welcome: URL flag ?welcome=1 → switch to Preferences and show banner once.
    useEffect(() => {
        if (searchParams.get('welcome') === '1') {
            setActiveTab('preferences');
            setWelcomeBannerOpen(true);
            searchParams.delete('welcome');
            setSearchParams(searchParams, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchTrip = useCallback(async () => {
        try {
            const { data } = await api.get(`/trips/${tripId}`);
            setTrip(data.trip);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Could not load trip');
        } finally {
            setLoading(false);
        }
    }, [tripId]);

    useEffect(() => { fetchTrip(); }, [fetchTrip]);

    // Handle Stripe checkout return: ?payment_session_id=... → poll status until paid/expired.
    useEffect(() => {
        const sessionId = searchParams.get('payment_session_id');
        const cancelled = searchParams.get('payment') === 'cancelled';
        if (cancelled) {
            toast('Payment cancelled', { description: 'No charge was made.', duration: 3000 });
            searchParams.delete('payment');
            setSearchParams(searchParams, { replace: true });
            return;
        }
        if (!sessionId) return;
        let attempts = 0;
        let stopped = false;
        setPaymentChecking(true);
        setActiveTab('budget');
        const tick = async () => {
            attempts += 1;
            try {
                const { data } = await api.get(`/payments/status/${sessionId}`);
                if (data.payment_status === 'paid' || data.premium_activated) {
                    if (!stopped) {
                        toast.success('Premium unlocked!', { description: 'AI cost breakdown, PDF export, and more are now available.', duration: 4500 });
                        setPaymentChecking(false);
                        searchParams.delete('payment_session_id');
                        setSearchParams(searchParams, { replace: true });
                        fetchTrip();
                        setRefreshKey((k) => k + 1);
                    }
                    return;
                }
                if (data.status === 'expired' || attempts >= 8) {
                    if (!stopped) {
                        toast.error('Payment did not complete. Try again from the upgrade modal.');
                        setPaymentChecking(false);
                        searchParams.delete('payment_session_id');
                        setSearchParams(searchParams, { replace: true });
                    }
                    return;
                }
                setTimeout(tick, 2000);
            } catch (e) {
                if (!stopped) {
                    setPaymentChecking(false);
                    toast.error(e?.response?.data?.detail || 'Could not verify payment');
                    searchParams.delete('payment_session_id');
                    setSearchParams(searchParams, { replace: true });
                }
            }
        };
        tick();
        return () => { stopped = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams.get('payment_session_id'), searchParams.get('payment')]);

    // Poll trip metadata every 5s; refresh full data when last_updated changes.
    usePolling(`/trips/${tripId}/poll`, {
        intervalMs: 5000,
        enabled: !!tripId && !!trip,
        onChange: () => {
            fetchTrip();
            setRefreshKey(k => k + 1);
            toast('Trip updated', { description: 'Someone made a change.', duration: 2500 });
        },
    });

    const inviteUrl = trip ? `${window.location.origin}/join/${trip.invite_code}` : '';

    const copyInvite = async () => {
        try {
            await navigator.clipboard.writeText(inviteUrl);
            setCopied(true);
            toast.success('Invite link copied');
            setTimeout(() => setCopied(false), 1800);
        } catch { toast.error('Copy failed'); }
    };

    const isOwner = !!(trip && user && trip.owner_id === user.user_id);

    if (loading) {
        return (
            <div className="min-h-screen bg-[var(--ns-bg)]">
                <Header />
                <div className="flex items-center justify-center py-32">
                    <div className="w-10 h-10 rounded-full border-2 border-[var(--ns-border)] border-t-[var(--ns-clay)] animate-spin" />
                </div>
            </div>
        );
    }

    if (!trip) {
        return (
            <div className="min-h-screen bg-[var(--ns-bg)]">
                <Header />
                <div className="max-w-7xl mx-auto px-4 py-16 text-center">
                    <h2 className="font-display text-2xl">Trip not found</h2>
                    <Link to="/dashboard" className="text-[var(--ns-clay)] mt-4 inline-block">Back to dashboard</Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[var(--ns-bg)] flex flex-col" data-testid={TRIP.detailContainer}>
            <Header />
            <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
                <Link to="/dashboard" className="inline-flex items-center text-sm text-[var(--ns-text2)] hover:text-[var(--ns-text)] transition mb-6">
                    <ArrowLeft size={14} className="mr-1" /> Back to dashboard
                </Link>

                {/* Hero */}
                <div className="ns-card p-6 sm:p-10">
                    <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
                        <div className="flex-1 min-w-0">
                            <div className="text-xs uppercase tracking-[0.18em] text-[var(--ns-clay)] font-medium flex items-center gap-1.5">
                                <MapPin size={12} /> {trip.destination}
                            </div>
                            <h1 className="font-display text-3xl sm:text-5xl font-semibold tracking-tight mt-2 text-[var(--ns-text)]">{trip.name}</h1>
                            <div className="flex flex-wrap items-center gap-4 mt-4 text-[var(--ns-text2)]">
                                <span className="inline-flex items-center gap-1.5 text-sm"><Calendar size={14} /> {formatDateRange(trip.start_date, trip.end_date)}</span>
                                <span className="inline-flex items-center gap-1.5 text-sm"><Users size={14} /> {trip.participants?.length || 1} traveler{(trip.participants?.length || 1) > 1 ? 's' : ''}</span>
                            </div>
                            <ParticipantList participants={trip.participants} ownerId={trip.owner_id} />

                            {/* Status indicators */}
                            <div data-testid={TRIP_STATUS.container} className="flex flex-wrap gap-2 mt-6">
                                <StatusPill icon={Bulb} label="Ideas" value={trip.ideas_count} testid={TRIP_STATUS.ideasCount} />
                                <StatusPill icon={ListChecks} label="Preferences" value={`${trip.preferences_submitted}/${trip.preferences_total}`} testid={TRIP_STATUS.preferencesCount} />
                                <StatusPill icon={Vote} label="Votes" value={`${trip.votes_cast}/${trip.votes_total}`} testid={TRIP_STATUS.votesCount} />
                                {trip.is_premium && (
                                    <span
                                        data-testid={UPGRADE.premiumBadge}
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-widest"
                                        style={{ background: 'var(--ns-clay)', color: 'white' }}
                                    >
                                        <Sparkles size={12} /> Premium
                                    </span>
                                )}
                            </div>
                        </div>

                        <div className="lg:max-w-xs w-full space-y-3">
                            <Button
                                onClick={copyInvite}
                                data-testid={TRIP.copyInviteButton}
                                className="w-full rounded-full h-11 text-white font-medium"
                                style={{ background: 'var(--ns-navy)' }}
                            >
                                {copied ? <><Check size={16} className="mr-2" /> Copied!</> : <><Share2 size={16} className="mr-2" /> Share invite</>}
                            </Button>
                            {!trip.is_premium && (
                                <Button
                                    onClick={() => setUpgradeOpen(true)}
                                    data-testid={UPGRADE.openButton}
                                    className="w-full rounded-full h-11 text-white font-medium"
                                    style={{ background: 'var(--ns-clay)' }}
                                >
                                    <Sparkles size={16} className="mr-2" /> Upgrade to Premium · $6.99
                                </Button>
                            )}
                            <Button
                                variant="outline"
                                onClick={() => setSettingsOpen(true)}
                                data-testid={TRIP_SETTINGS.openButton}
                                className="w-full rounded-full h-11 border-[var(--ns-border)] text-[var(--ns-navy)]"
                            >
                                <Settings size={16} className="mr-2" /> Manage members
                            </Button>
                            <code
                                data-testid={TRIP.inviteLink}
                                className="block truncate text-xs bg-[var(--ns-soft)] border border-[var(--ns-border)] rounded-xl px-3 py-2 font-mono text-[var(--ns-text3)]"
                            >
                                {inviteUrl}
                            </code>
                        </div>
                    </div>
                </div>

                {/* Tabs */}
                <TripDetailsSection tripId={trip.trip_id} />

                <div className="mt-10">
                    <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                        <TabsList className="bg-[var(--ns-soft)] rounded-full p-1 h-auto flex flex-wrap gap-1">
                            <TabsTrigger value="ideas" data-testid={TRIP.tabIdeas} className="rounded-full px-5 py-2 data-[state=active]:bg-white data-[state=active]:text-[var(--ns-navy)] data-[state=active]:shadow-sm">Ideas</TabsTrigger>
                            <TabsTrigger value="preferences" data-testid={TRIP.tabPreferences} className="rounded-full px-5 py-2 data-[state=active]:bg-white data-[state=active]:text-[var(--ns-navy)] data-[state=active]:shadow-sm">Preferences</TabsTrigger>
                            {(trip.participants?.filter(p => p.preferences_submitted).length || 0) >= 2 && (
                                <TabsTrigger value="group" data-testid={GROUP_PROFILE.tab} className="rounded-full px-5 py-2 data-[state=active]:bg-white data-[state=active]:text-[var(--ns-navy)] data-[state=active]:shadow-sm">
                                    Group Profile
                                </TabsTrigger>
                            )}
                            <TabsTrigger value="votes" data-testid={TRIP.tabVotes} className="rounded-full px-5 py-2 data-[state=active]:bg-white data-[state=active]:text-[var(--ns-navy)] data-[state=active]:shadow-sm">Votes</TabsTrigger>
                            <TabsTrigger value="itinerary" data-testid={TRIP.tabItinerary} className="rounded-full px-5 py-2 data-[state=active]:bg-white data-[state=active]:text-[var(--ns-navy)] data-[state=active]:shadow-sm">Itinerary</TabsTrigger>
                            <TabsTrigger value="budget" data-testid={TRIP.tabBudget} className="rounded-full px-5 py-2 data-[state=active]:bg-white data-[state=active]:text-[var(--ns-navy)] data-[state=active]:shadow-sm">Budget</TabsTrigger>
                        </TabsList>
                        <TabsContent value="ideas">
                            <IdeasTab
                                tripId={trip.trip_id}
                                refreshKey={refreshKey}
                                onOpenSave={() => setSaveIdeaOpen(true)}
                                currentUserId={user?.user_id}
                                tripOwnerId={trip.owner_id}
                            />
                        </TabsContent>
                        <TabsContent value="preferences">
                            <PreferencesTab
                                tripId={trip.trip_id}
                                trip={trip}
                                currentUserId={user?.user_id}
                                onSubmitted={() => { fetchTrip(); setRefreshKey(k => k + 1); }}
                                showWelcomeBanner={welcomeBannerOpen}
                                onDismissWelcome={() => setWelcomeBannerOpen(false)}
                            />
                        </TabsContent>
                        <TabsContent value="group">
                            <GroupProfileTab tripId={trip.trip_id} trip={trip} refreshKey={refreshKey} />
                        </TabsContent>
                        <TabsContent value="votes">
                            <VotesTab tripId={trip.trip_id} trip={trip} refreshKey={refreshKey} participantCount={trip.participants?.length || 1} />
                        </TabsContent>
                        <TabsContent value="itinerary">
                            <ItineraryTab trip={trip} refreshKey={refreshKey} onUpgrade={() => setUpgradeOpen(true)} />
                        </TabsContent>
                        <TabsContent value="budget">
                            <BudgetTab
                                trip={trip}
                                refreshKey={refreshKey}
                                isOwner={isOwner}
                                onUpgrade={() => setUpgradeOpen(true)}
                                onTripUpdated={(t) => setTrip(t)}
                            />
                        </TabsContent>
                    </Tabs>
                </div>
            </main>

            <SaveIdeaDialog
                open={saveIdeaOpen}
                onOpenChange={setSaveIdeaOpen}
                tripId={trip.trip_id}
                onSaved={() => { fetchTrip(); setRefreshKey(k => k + 1); }}
            />
            <TripSettingsDialog
                open={settingsOpen}
                onOpenChange={setSettingsOpen}
                trip={trip}
                isOwner={isOwner}
                currentUserId={user?.user_id}
                onUpdated={(t) => setTrip(t)}
            />
            <UpgradeModal
                open={upgradeOpen}
                onOpenChange={setUpgradeOpen}
                tripId={trip.trip_id}
                tripName={trip.name}
            />
            {paymentChecking && (
                <div
                    data-testid={UPGRADE.paymentProcessing}
                    className="fixed inset-0 z-[200] bg-black/40 backdrop-blur-sm flex items-center justify-center"
                >
                    <div className="bg-white rounded-3xl px-8 py-7 max-w-sm w-full mx-4 text-center shadow-xl">
                        <Loader2 size={28} className="mx-auto animate-spin text-[var(--ns-clay)]" />
                        <h3 className="font-display text-xl font-semibold tracking-tight mt-4">Verifying your payment</h3>
                        <p className="text-sm text-[var(--ns-text2)] mt-2">Hang tight — we're confirming with Stripe and unlocking your trip.</p>
                    </div>
                </div>
            )}
            <Footer />
        </div>
    );
}
